import React, { useState, useEffect } from 'react';
import { resumeService, adminService, ActivitySummary } from '../../services/api';
import toast from 'react-hot-toast';
import { Activity, Users, AlertTriangle, TrendingUp, RefreshCw, UserCheck, UserX } from 'lucide-react';

interface FeatureBar {
  feature: string;
  success: number;
  error: number;
}

const AdminStatsTab: React.FC = () => {
  const [summary, setSummary] = useState<ActivitySummary | null>(null);
  const [totalUsers, setTotalUsers] = useState<number | null>(null);
  const [activeUsers, setActiveUsers] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [maxDaily, setMaxDaily] = useState(1);

  const fetchSummary = async () => {
    setIsLoading(true);
    try {
      const [res, usersRes] = await Promise.all([
        resumeService.getActivitySummary(),
        adminService.listUsers(0, 1),
      ]);
      setSummary(res.data);
      setTotalUsers(usersRes.data.data.totalUsers);
      setActiveUsers(usersRes.data.data.activeUsers);
      const max = Math.max(1, ...res.data.daily.map((d) => d.count));
      setMaxDaily(max);
    } catch (error) {
      console.error('Failed to fetch summary:', error);
      toast.error('Failed to load statistics');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  if (isLoading) {
    return <div className="text-center p-12 text-secondary-foreground">Loading statistics...</div>;
  }

  if (!summary) {
    return <div className="bg-white rounded-card shadow-soft text-center p-12 text-secondary-foreground">No statistics available</div>;
  }

  const featureMap = new Map<string, FeatureBar>();
  for (const row of summary.byFeature) {
    const key = row.feature || 'unknown';
    const entry = featureMap.get(key) ?? { feature: key, success: 0, error: 0 };
    if (row.success) entry.success += row.count; else entry.error += row.count;
    featureMap.set(key, entry);
  }
  const featureBars = Array.from(featureMap.values()).sort((a, b) => (b.success + b.error) - (a.success + a.error));
  const maxFeature = Math.max(1, ...featureBars.map((f) => f.success + f.error));

  const statCards = [
    { label: 'Total Users', value: (totalUsers ?? summary.distinctUsers).toLocaleString(), icon: Users, color: 'text-brand-600 bg-brand-50' },
    { label: 'Active Users', value: (activeUsers ?? summary.distinctUsers).toLocaleString(), icon: UserCheck, color: 'text-emerald-600 bg-emerald-50' },
    { label: 'Inactive Users', value: ((totalUsers ?? summary.distinctUsers) - (activeUsers ?? 0)).toLocaleString(), icon: UserX, color: 'text-zinc-500 bg-zinc-100' },
    { label: 'Total Events', value: summary.totalEvents.toLocaleString(), icon: Activity, color: 'text-sky-600 bg-sky-50' },
    { label: 'Errors', value: summary.totalErrors.toLocaleString(), icon: AlertTriangle, color: 'text-red-500 bg-red-50' },
    { label: 'Users with Activity', value: summary.distinctUsers.toLocaleString(), icon: TrendingUp, color: 'text-amber-600 bg-amber-50' },
  ];

  return (
    <>
      <div className="flex justify-end mb-6">
        <button
          onClick={fetchSummary}
          className="h-10 px-4 bg-white border border-zinc-200 rounded-btn text-sm text-foreground hover:bg-subtle inline-flex items-center gap-1.5"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {statCards.map((card) => (
          <div key={card.label} className="bg-white rounded-card p-5 shadow-soft">
            <div className={`inline-flex items-center justify-center w-10 h-10 rounded-btn ${card.color} mb-3`}>
              <card.icon className="w-5 h-5" />
            </div>
            <div className="text-2xl font-semibold text-foreground font-display">{card.value}</div>
            <div className="text-sm text-secondary-foreground mt-0.5">{card.label}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-card p-6 shadow-soft">
          <h2 className="text-lg font-semibold text-foreground mb-4 font-display">Activity — Last 30 Days</h2>
          <div className="flex items-end gap-[3px] h-40">
            {summary.daily.map((day) => {
              const height = day.count === 0 ? 2 : Math.max(4, Math.round((day.count / maxDaily) * 152));
              return (
                <div
                  key={day.date}
                  className="flex-1 bg-brand-200 hover:bg-brand-500 rounded-t-[3px] transition-colors relative group"
                  style={{ height: `${height}px` }}
                  title={`${day.date}: ${day.count}`}
                />
              );
            })}
          </div>
          <div className="mt-2 text-xs text-secondary-foreground flex justify-between">
            <span>{summary.daily[0]?.date}</span>
            <span>Today</span>
          </div>
        </div>

        <div className="bg-white rounded-card p-6 shadow-soft">
          <h2 className="text-lg font-semibold text-foreground mb-4 font-display">Feature Usage</h2>
          {featureBars.length === 0 ? (
            <div className="text-center p-8 text-secondary-foreground">No data yet</div>
          ) : (
            <div className="space-y-3">
              {featureBars.map((bar) => {
                const total = bar.success + bar.error;
                const successPct = total === 0 ? 0 : Math.round((bar.success / maxFeature) * 100);
                const errorPct = total === 0 ? 0 : Math.round((bar.error / maxFeature) * 100);
                return (
                  <div key={bar.feature}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-foreground font-medium truncate">{bar.feature || 'unknown'}</span>
                      <span className="text-secondary-foreground ml-2 whitespace-nowrap">
                        {total} <span className="text-red-400">({bar.error} err)</span>
                      </span>
                    </div>
                    <div className="flex h-2.5 bg-zinc-100 rounded-full overflow-hidden">
                      <div className="bg-brand-500 transition-all" style={{ width: `${successPct}%` }} />
                      <div className="bg-red-400 transition-all" style={{ width: `${errorPct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white rounded-card p-6 shadow-soft">
          <h2 className="text-lg font-semibold text-foreground mb-4 font-display">Per-User / Event Counts</h2>
          {summary.byUserAndEvent.length === 0 ? (
            <div className="text-center p-8 text-secondary-foreground">No data yet</div>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr>
                    <th className="text-left py-2 text-secondary-foreground font-medium border-b border-zinc-100">User</th>
                    <th className="text-left py-2 text-secondary-foreground font-medium border-b border-zinc-100">Event</th>
                    <th className="text-right py-2 text-secondary-foreground font-medium border-b border-zinc-100">Count</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.byUserAndEvent.map((row, i) => (
                    <tr key={i} className="border-b border-zinc-50 last:border-0">
                      <td className="py-2 font-mono text-xs">{row.userId.slice(0, 8)}…</td>
                      <td className="py-2">{row.event}</td>
                      <td className="py-2 text-right font-medium">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="bg-white rounded-card p-6 shadow-soft">
          <h2 className="text-lg font-semibold text-foreground mb-4 font-display">Recent Errors</h2>
          {summary.recentErrors.length === 0 ? (
            <div className="text-center p-8 text-secondary-foreground">No recent errors — nice!</div>
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {summary.recentErrors.map((err) => (
                <div key={err.id} className="border border-red-100 bg-red-50/50 rounded-btn p-3">
                  <div className="flex justify-between items-center gap-2">
                    <span className="font-medium text-red-600 text-sm truncate">{err.event}</span>
                    <span className="text-xs text-secondary-foreground whitespace-nowrap">
                      {new Date(err.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-xs text-secondary-foreground mt-1 truncate">
                    {err.feature || ''}{err.feature && err.path ? ' · ' : ''}{err.path || ''}
                  </div>
                  {err.error && <div className="text-xs text-red-500 mt-1 truncate">{err.error}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminStatsTab;
